import { useDispatch } from "react-redux";
import { useLocation, useMatch, useNavigate } from "react-router-dom";
import { type IState } from "..";
import { useSelectorEq } from "commons/store/common";
import {
  apiGChannelList,
  apiGnewNoticeCheck,
  apiGuserProfileInfoCheck,
  rdxIsLeft,
  rdxResetChannelList,
} from "./navigatesR";
import { type INavigatesVO } from "./navigateVo";
import { useApi } from "../common";
import { useCallback, useEffect, useState } from "react";
import { Grade } from "../auth/authVo";
import useCookies from "commons/cookies/cookiesHook";

/**
 * 상단 네비게이션 라우터이동 evnet함수
 */
export const useTopSelected = () => {
  const location = useLocation();
  return () => {
    const path = location.pathname.split("/");
    return path[2];
  };
};

/**
 * 1deps 네비게이션 정보와 evnet함수
 */
export const useLeft = () => {
  const { isLeft } = useSelectorEq((state: IState) => ({
    isLeft: state.navigates.isLeft,
  }));
  const dispatch = useDispatch();
  const activeLeft = async (active: boolean) => {
    dispatch(rdxIsLeft(active));
  };
  return { isLeft, activeLeft };
};

/**
 * 1deps 네비게이션 정보 반환
 */
export const useLeftList = () => {
  const { leftList, flag } = useSelectorEq((state: IState) => ({
    leftList: state.navigates.leftList,
    flag: state.navigates.userProfileFlag,
  }));

  return { leftList, flag };
};

/**
 * 1deps 네비게이션 active설정과 router이동 event
 */
export const useLeftSelected = () => {
  const navigate = useNavigate();
  const getLeftSelected = (item: INavigatesVO) => {
    const matched = useMatch(item.match ? item.match : item.router);
    return matched;
  };
  const setLeft = async (selected: INavigatesVO) => {
    if (!selected.disabled) {
      navigate(selected.router);
    }
  };
  return { getLeftSelected, setLeft };
};

/**
 * 2deps 네비게이션 정보 반환
 */
export const useSecList = (id: string) => {
  const { secList } = useSelectorEq((state: IState) => ({
    secList: state.navigates.secList[id] as INavigatesVO[] | undefined,
  }));
  return secList;
};

/**
 * 2deps 네비게이션 active설정과 router이동 event
 */
export const useSecSelected = () => {
  const navigate = useNavigate();
  const getSecSelected = (router: string) => {
    const matched = useMatch(router);
    return matched;
  };
  const setSec = async (selected: INavigatesVO) => {
    if (!selected.disabled) {
      navigate(selected.router);
    }
  };
  return { getSecSelected, setSec };
};

export const useNewNoticeCheckInit = () => {
  const { newNotice } = useSelectorEq((state: IState) => ({
    newNotice: state.navigates.newNotice,
  }));
  return { newNotice };
};

/**
 * 공지사항 새로운 알림 있는지 체크
 */
export const useNewNotice = () => {
  const { role } = useSelectorEq((state: IState) => ({
    newNotice: state.navigates.newNotice,
    role: state.auth.role,
  }));
  const { noticeCheck } = useNewNoticeCheck();

  useEffect(() => {
    if (role !== Grade.MASTER) {
      noticeCheck();
    }
  }, []);
};

export const useNewNoticeCheck = () => {
  const { apiResult } = useApi();
  const noticeCheck = async () => {
    try {
      await apiResult(apiGnewNoticeCheck);
    } catch (error) {
      throw new Error("API Error: apiGnewNoticeCheck");
    }
  };
  return { noticeCheck };
};

/**
 * 사용자 일 경우 프로필 입력 했는지 안했는지 체크
 */
export const useUserProfileCheck = (role: string) => {
  const { check } = useProfileInfoCheck();

  useEffect(() => {
    if (role !== Grade.MASTER) {
      check();
    }
  }, []);
};

export const useProfileInfoCheck = () => {
  const { apiResult } = useApi();
  const check = async () => {
    try {
      await apiResult(apiGuserProfileInfoCheck);
    } catch (error) {
      throw new Error("API Error: apiGuserProfileInfoCheck");
    }
  };
  return { check };
};

/**
 * 사용자가 프로필 입력이 최초인지 확인(joyride 실행 여부)
 */
export const useUserProfileNewWrite = () => {
  const [isNewWrite, setIsNewWrite] = useState<string | undefined>(""); // 기본값은 false로 설정
  const { get } = useCookies();
  useEffect(() => {
    const fetchData = async () => {
      const isNewWriteCookie = await get("isNewWrite");
      setIsNewWrite(isNewWriteCookie);
    };

    fetchData();
  }, []);

  return isNewWrite;
};

// joyride 판별 쿠키 값을 삭제하는 hook
export const useDeleteCookieNewWrite = () => {
  const { remove } = useCookies();

  const removeCookie = useCallback(() => {
    remove("isNewWrite");
  }, [remove]);

  return removeCookie;
};

/**
 * 사이드바 > 채널 선택 팝업
 * @returns
 */
export const useChannelListLoad = () => {
  const { apiResult } = useApi();

  return async (type?: "PROVIDER" | "MEDIA" | "PARTNERS") => {
    await apiResult(apiGChannelList, { type: type });
  };
};

export const useSidebarChannelPop = () => {
  const dispatch = useDispatch();

  const channelListLoad = useChannelListLoad();

  useEffect(() => {
    channelListLoad();

    return () => {
      dispatch(rdxResetChannelList());
    };
  }, []);
};

export const useSelectSideNav = () => {
  const { selectChannel, channelList } = useSelectorEq((state: IState) => ({
    selectChannel: state.navigates.selectChannel,
    channelList: state.navigates.channelList,
  }));

  return { selectChannel, channelList };
};

export const useAlarmFlag = () => {
  const { alarmFlag, isAlarm } = useSelectorEq((state: IState) => ({
    alarmFlag: state.navigates.alarmFlag,
    isAlarm: state.navigates.isAlarm,
  }));
  return { alarmFlag, isAlarm };
};
